/**
 * Break-even engine.
 *
 * Composes the mortgage, opportunity-cost portfolio, and transaction-friction
 * models into a single side-by-side comparison of renting vs. owning the
 * *same* home over a fixed horizon.
 *
 * Framing (matches docs/requrements.md — "Functional Requirements"):
 *
 *  • Both actors start with identical wealth = down payment + closing costs.
 *    The buyer spends it acquiring the home; the renter invests it at the
 *    discount rate. This is the classic opportunity-cost anchor and the only
 *    way to compare the two paths fairly.
 *
 *  • Each month, each actor pays their housing bill:
 *      rent path — rent (annual step, escalates on 12-month anniversaries)
 *                 + renter's insurance;
 *      own path  — mortgage P&I (from the amortization schedule)
 *                 + property tax on current market value
 *                 + home insurance
 *                 + maintenance on current market value
 *                 + HOA.
 *
 *  • Whichever path is cheaper that month, the difference goes into the
 *    cheaper path's investment portfolio (both compound at the same
 *    discount rate). This preserves symmetry and prevents the model from
 *    silently discarding the buyer's late-horizon cash advantage. Documented
 *    here so a skeptical reader can see the assumption without reading code.
 *
 *  • Net worth at month t:
 *      rent path = portfolio(t)
 *      own path  = home_value(t) − loan_balance(t) − sell_cost(home_value(t))
 *                 + portfolio(t)
 *    Sell-cost is applied hypothetically at every t so the curve reflects
 *    what the owner would net if they sold *today*. This is the honest way
 *    to compare — waiting until the horizon to subtract friction hides how
 *    long it takes owning to break even.
 *
 *  • Break-even year Y = the smallest year boundary at which own_nw ≥ rent_nw.
 *    Returns null when owning never overtakes renting inside the horizon.
 *
 * The function is pure: same inputs → same outputs, no I/O, no Date, no
 * randomness. This is what lets the golden-number regression test pin the
 * canonical scenario down to the cent.
 */

import type { Jurisdiction } from "./jurisdictions";
import { amortizeMortgage } from "./mortgage";
import { buyCost, sellCost } from "./transactions";

const MONTHS_PER_YEAR = 12;
const PERCENT_DIVISOR = 100;

export interface BreakEvenInputs {
  /** Purchase price of the home in local currency. */
  readonly homePrice: number;
  /** Down payment as a percentage of purchase price (e.g. 20 for 20%). */
  readonly downPaymentPercent: number;
  /** Nominal annual mortgage interest rate as a percentage (e.g. 6.5 for 6.5%). */
  readonly annualInterestRatePercent: number;
  /** Mortgage term in whole years. */
  readonly termYears: number;
  /** Starting rent per month (before any inflation). */
  readonly monthlyRent: number;
  /** Annual rent inflation as a percentage (applied at 12-month anniversaries). */
  readonly annualRentInflationPercent: number;
  /** Annual home price appreciation as a percentage (compounded monthly). */
  readonly annualHomeAppreciationPercent: number;
  /** Nominal annualized discount / expected investment return, as a percentage (compounded monthly). */
  readonly annualDiscountRatePercent: number;
  /** Effective annual property-tax rate on current market value, as a percentage. */
  readonly annualPropertyTaxRatePercent: number;
  /** Annual homeowner's insurance premium (nominal, currency). */
  readonly annualHomeInsurance: number;
  /** Annual maintenance spend as a percentage of current market value (rule-of-thumb ~1%). */
  readonly annualMaintenanceRatePercent: number;
  /** Fixed monthly HOA / condo fees. */
  readonly monthlyHOA: number;
  /** Annual renter's insurance premium. */
  readonly annualRentersInsurance: number;
  /** Horizon in whole years. Cashflow and net-worth arrays span 1..horizonYears*12 months. */
  readonly horizonYears: number;
  /** Jurisdiction that supplies closing-cost, transfer-tax, and selling-commission rates. */
  readonly jurisdiction: Jurisdiction;
  /**
   * "Indefinite hold" mode. When true, the owner is assumed never to sell, so
   * no hypothetical selling costs are subtracted from the own-path net worth.
   * The rest of the model is unchanged — the owner still accrues equity, still
   * pays every recurring cost, and still banks any monthly cashflow advantage
   * into the investment portfolio at the discount rate.
   *
   * Defaults to `false` — the honest comparison is "what if you sold today,"
   * because it exposes the transaction-friction penalty the buyer has to earn
   * back before overtaking the renter's head-start.
   */
  readonly neverSell?: boolean;
}

/** One month's value for each of the two paths. Used by both cashflow and net-worth series. */
export interface BreakEvenMonthPoint {
  /** 1-indexed month number. Month 12 is the end of year 1. */
  readonly month: number;
  /** Value on the rent path at this month. */
  readonly rentPath: number;
  /** Value on the own path at this month. */
  readonly ownPath: number;
}

export interface BreakEvenResult {
  /** Per-month gross housing outlay for each path. Recurring costs only (initial closing costs are accounted for via the initial capital pool, not here). */
  readonly monthlyCashflow: readonly BreakEvenMonthPoint[];
  /** Per-month cumulative net worth for each path. See the module docstring for the definition. */
  readonly cumulativeNetWorth: readonly BreakEvenMonthPoint[];
  /** Smallest year Y in [1, horizonYears] at which own_nw ≥ rent_nw. `null` if owning never overtakes renting inside the horizon. */
  readonly breakEvenYear: number | null;
}

/**
 * Defensible recurring-cost defaults for the calculator. Every value here
 * has a corresponding citation on the methodology page. Not exported from
 * `MORTGAGE_DEFAULTS` because these are properties of the *scenario*
 * (property, insurance market, ownership overhead), not of the loan
 * product itself.
 */
export const BREAK_EVEN_DEFAULTS = {
  /** US national average effective property-tax rate (~1.1% per Tax Foundation 2024). */
  annualPropertyTaxRatePercent: 1.1,
  /** US national average annual homeowner's insurance premium (Insurance Information Institute 2024). */
  annualHomeInsurance: 1500,
  /** Standard rule-of-thumb maintenance reserve (~1% of home value per year). */
  annualMaintenanceRatePercent: 1.0,
  /** No HOA by default — most single-family purchases have none. */
  monthlyHOA: 0,
  /** ~$15/mo renter's insurance baseline (Insurance Information Institute 2024). */
  annualRentersInsurance: 180,
  /** 30-year horizon matches the default mortgage term. */
  horizonYears: 30,
} as const;

export function computeBreakEven(inputs: BreakEvenInputs): BreakEvenResult {
  const {
    homePrice,
    downPaymentPercent,
    annualInterestRatePercent,
    termYears,
    monthlyRent,
    annualRentInflationPercent,
    annualHomeAppreciationPercent,
    annualDiscountRatePercent,
    annualPropertyTaxRatePercent,
    annualHomeInsurance,
    annualMaintenanceRatePercent,
    monthlyHOA,
    annualRentersInsurance,
    horizonYears,
    jurisdiction,
    neverSell = false,
  } = inputs;

  const horizonMonths = Math.round(horizonYears * MONTHS_PER_YEAR);
  const downPayment = homePrice * (downPaymentPercent / PERCENT_DIVISOR);
  const closing = buyCost(homePrice, jurisdiction);
  const initialCapital = downPayment + closing.total;

  const schedule = amortizeMortgage({
    homePrice,
    downPayment,
    annualInterestRatePercent,
    termYears,
  });

  const monthlyDiscountRate =
    annualDiscountRatePercent / PERCENT_DIVISOR / MONTHS_PER_YEAR;
  const monthlyAppreciationFactor = Math.pow(
    1 + annualHomeAppreciationPercent / PERCENT_DIVISOR,
    1 / MONTHS_PER_YEAR,
  );
  const rentInflationFactor = 1 + annualRentInflationPercent / PERCENT_DIVISOR;

  const monthlyHomeInsurance = annualHomeInsurance / MONTHS_PER_YEAR;
  const monthlyRentersInsurance = annualRentersInsurance / MONTHS_PER_YEAR;

  const cashflow: BreakEvenMonthPoint[] = new Array(horizonMonths);
  const netWorth: BreakEvenMonthPoint[] = new Array(horizonMonths);

  let rentPortfolio = initialCapital;
  let ownPortfolio = 0;

  for (let month = 1; month <= horizonMonths; month++) {
    // Rent steps annually on 12-month anniversaries (yearIndex 0 for months 1–12).
    const yearIndex = Math.floor((month - 1) / MONTHS_PER_YEAR);
    const currentRent = monthlyRent * Math.pow(rentInflationFactor, yearIndex);
    const rentPayment = currentRent + monthlyRentersInsurance;

    const currentHomeValue = homePrice * Math.pow(monthlyAppreciationFactor, month);
    const monthlyPropertyTax =
      (currentHomeValue * (annualPropertyTaxRatePercent / PERCENT_DIVISOR)) /
      MONTHS_PER_YEAR;
    const monthlyMaintenance =
      (currentHomeValue * (annualMaintenanceRatePercent / PERCENT_DIVISOR)) /
      MONTHS_PER_YEAR;

    // P&I from the amortization schedule; zero after the loan is paid off.
    let piPayment = 0;
    let loanBalance = 0;
    if (month <= schedule.months) {
      const row = schedule.schedule[month - 1];
      if (row) {
        piPayment = row.interest + row.principal;
        loanBalance = row.balance;
      }
    }

    const ownPayment =
      piPayment +
      monthlyPropertyTax +
      monthlyHomeInsurance +
      monthlyMaintenance +
      monthlyHOA;

    // Symmetric investment: whichever path is cheaper this month invests the
    // difference at the same discount rate. Portfolios follow the ordinary-
    // annuity convention — grow first on the prior balance, then apply the
    // contribution at period end. Matches `projectPortfolio` in portfolio.ts.
    const rentContribution = Math.max(0, ownPayment - rentPayment);
    const ownContribution = Math.max(0, rentPayment - ownPayment);
    rentPortfolio = rentPortfolio * (1 + monthlyDiscountRate) + rentContribution;
    ownPortfolio = ownPortfolio * (1 + monthlyDiscountRate) + ownContribution;

    // Indefinite-hold mode skips the hypothetical selling-cost drag; every
    // other line of the model is identical. This is the only difference the
    // "never sell" toggle makes.
    const sellFriction = neverSell
      ? 0
      : sellCost(currentHomeValue, jurisdiction).total;
    const ownNetWorth =
      currentHomeValue - loanBalance - sellFriction + ownPortfolio;

    cashflow[month - 1] = { month, rentPath: rentPayment, ownPath: ownPayment };
    netWorth[month - 1] = { month, rentPath: rentPortfolio, ownPath: ownNetWorth };
  }

  let breakEvenYear: number | null = null;
  for (let y = 1; y <= horizonYears; y++) {
    const idx = y * MONTHS_PER_YEAR - 1;
    const point = netWorth[idx];
    if (!point) break;
    if (point.ownPath >= point.rentPath) {
      breakEvenYear = y;
      break;
    }
  }

  return {
    monthlyCashflow: cashflow,
    cumulativeNetWorth: netWorth,
    breakEvenYear,
  };
}
